// src/pages/SolutionDetailPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { solutionService } from '../api';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';
import SocialShare from '../components/common/SocialShare';

// Styled components
const SolutionSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 0.5rem;
  }
`;

const SolutionHeader = styled.div`
  margin-bottom: 3rem;
  text-align: center;
`;

const SolutionTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SolutionSubtitle = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
`;

const SolutionHero = styled.div`
  position: relative;
  height: 500px;
  margin-bottom: 3rem;
  border-radius: 8px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const HeroImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.src || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center;
`;

const HeroVideo = styled.div`
  width: 100%;
  height: 100%;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const SolutionContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const SolutionDescription = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.8;
  margin-bottom: 2rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.8rem;
    margin: 2rem 0 1rem;
    color: ${props => props.theme.primary};
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 1.8rem 0 1rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const TechTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2rem 0;
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  background: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const CasesSection = styled.div`
  margin-top: 3rem;
`;

const CasesTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.accent};
  }
`;

const CaseCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  margin-bottom: 2rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CaseHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.text}10;
`;

const CaseTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
`;

const CaseSubtitle = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
`;

const CaseBody = styled.div`
  padding: 1.5rem;
  color: ${props => props.theme.text};
  
  p {
    margin-bottom: 1rem;
    line-height: 1.7;
  }
`;

const CaseImage = styled.div`
  height: 250px;
  background-image: url(${props => props.src || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center;
`;

const SidebarContent = styled.div``;

const SidebarCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.accent};
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    padding: 0.8rem 0;
    border-bottom: 1px solid ${props => props.theme.text}10;
    color: ${props => props.theme.text};
    display: flex;
    align-items: flex-start;
    
    &:last-child {
      border-bottom: none;
    }
    
    i {
      color: ${props => props.theme.primary};
      margin-right: 1rem;
      margin-top: 0.2rem;
    }
  }
`;

const ContactButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ContactButton = styled.a`
  display: inline-block;
  padding: 0.8rem 1.5rem;
  background-color: ${props => props.primary ? props.theme.primary : 'transparent'};
  color: ${props => props.primary ? 'white' : props.theme.primary};
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.2s ease;
  text-align: center;
  border: 1px solid ${props => props.theme.primary};
  
  &:hover {
    background-color: ${props => props.primary ? props.theme.primaryDark || props.theme.primary + 'dd' : props.theme.primary + '10'};
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none'};
  }
`;

const SolutionDetailPage = () => {
  const { solutionSlug } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [solution, setSolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Chargement des détails de la solution
  useEffect(() => {
    const fetchSolution = async () => {
      try {
        setLoading(true);
        
        const response = await solutionService.getById(solutionSlug);
        console.log("Solution data:", response.data);
        setSolution(response.data);
        
        // Mise à jour du titre de la page
        document.title = `${response.data.name || response.data.title} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching solution details:", err);
        setError(err.response?.data?.detail || t('errorLoadingSolution'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchSolution();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [solutionSlug, t]);
  
  // Extraire les technologies en tableau
  const getTechTags = (technologies) => {
    if (!technologies) return [];
    return typeof technologies === 'string' 
      ? technologies.split(',').map(tech => tech.trim())
      : Array.isArray(technologies) 
        ? technologies 
        : [];
  };
  
  // Fonction pour extraire les fonctionnalités de la description
  const extractFeatures = (description) => {
    if (!description) return [];
    
    // Recherche des listes à puces dans la description
    const features = [];
    const lines = description.split('\n');
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        features.push(trimmedLine.substring(2));
      }
    }
    
    // Si on n'a pas trouvé de liste à puces, on peut essayer de chercher une section "Fonctionnalités"
    if (features.length === 0 && description && description.includes('Fonctionnalités:')) {
      const featuresSection = description.split('Fonctionnalités:')[1].split('\n\n')[0];
      return featuresSection.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
    }
    
    return features;
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay 
      error={error} 
      backLink="/solutions" 
      backLinkText={t('backToSolutions')} 
    />;
  }
  
  if (!solution) return null;
  
  // Extraire les technologies
  const technologies = getTechTags(solution.technologies);
  
  // Extraire les fonctionnalités
  const features = extractFeatures(solution.description);
  
  return (
    <SolutionSection>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs theme={theme}>
            <a href="/">{t('home')}</a> <span>/</span> 
            <a href="/solutions">{t('solutions')}</a> <span>/</span> 
            <span>{solution.name || solution.title}</span>
          </Breadcrumbs>
          
          <SolutionHeader>
            <SolutionTitle theme={theme}>{solution.name || solution.title}</SolutionTitle>
            <SolutionSubtitle theme={theme}>{solution.short_description}</SolutionSubtitle>
          </SolutionHeader>
          
          <SolutionHero>
            {solution.video_url ? (
              <HeroVideo>
                <iframe
                  src={solution.video_url}
                  title={solution.name || solution.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </HeroVideo>
            ) : (
              <HeroImage src={solution.image} />
            )}
          </SolutionHero>
          
          <SolutionContent>
            <MainContent>
              <SocialShare 
                title={solution.name || solution.title}
                hashtags={['PropentaTech', 'Solution', (solution.name || solution.title).replace(/\s+/g, '')]}
              />
              
              <SolutionDescription theme={theme} dangerouslySetInnerHTML={{ __html: solution.description }} />
              
              {technologies.length > 0 && (
                <>
                  <h2 style={{ color: theme.primary, marginBottom: '1rem' }}>{t('technologiesUsed')}</h2>
                  <TechTagsContainer>
                    {technologies.map((tech, index) => (
                      <TechTag key={index} theme={theme}>{tech}</TechTag>
                    ))}
                  </TechTagsContainer>
                </>
              )}
              
              {solution.cases && solution.cases.length > 0 && (
                <CasesSection>
                  <CasesTitle theme={theme}>{t('useCases')}</CasesTitle>
                  
                  {solution.cases.map(caseItem => (
                    <CaseCard key={caseItem.id} theme={theme}>
                      {caseItem.image && <CaseImage src={caseItem.image} />}
                      <CaseHeader theme={theme}>
                        <CaseTitle theme={theme}>{caseItem.title}</CaseTitle>
                        {caseItem.client && (
                          <CaseSubtitle theme={theme}>{t('client')}: {caseItem.client}</CaseSubtitle>
                        )}
                      </CaseHeader>
                      <CaseBody theme={theme}>
                        <p>{caseItem.description}</p>
                        <h4 style={{ color: theme.primary, marginBottom: '0.8rem' }}>{t('results')}</h4>
                        <p>{caseItem.results}</p>
                      </CaseBody>
                    </CaseCard>
                  ))}
                </CasesSection>
              )}
              
              <div style={{ marginTop: '3rem', textAlign: 'center' }}>
                <ContactButton
                  href="/contact"
                  theme={theme}
                  primary
                >
                  <i className="fas fa-paper-plane" style={{ marginRight: '8px' }}></i>
                  {t('contactUsForSolution')}
                </ContactButton>
              </div>
            </MainContent>
            
            <SidebarContent>
              {features.length > 0 && (
                <SidebarCard theme={theme}>
                  <SidebarTitle theme={theme}>{t('keyFeatures')}</SidebarTitle>
                  <FeaturesList theme={theme}>
                    {features.map((feature, index) => (
                      <li key={index}>
                        <i className="fas fa-check-circle"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </FeaturesList>
                </SidebarCard>
              )}
              
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('interestedInSolution')}</SidebarTitle>
                <p style={{ color: theme.text, marginBottom: '1.5rem' }}>
                  {t('interestedInSolutionDesc')}
                </p>
                <ContactButtons>
                  <ContactButton
                    href="/contact"
                    theme={theme}
                    primary
                  >
                    {t('requestDemo')}
                  </ContactButton>
                  <ContactButton
                    href="/contact"
                    theme={theme}
                  >
                    {t('getQuote')}
                  </ContactButton>
                </ContactButtons>
              </SidebarCard>
              
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('relatedSolutions')}</SidebarTitle>
                <div style={{ marginTop: '1.5rem' }}>
                  <p style={{ color: theme.text, textAlign: 'center' }}>
                    {t('exploreOtherSolutions')}
                  </p>
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <ContactButton
                      href="/solutions"
                      theme={theme}
                    >
                      {t('viewAllSolutions')}
                    </ContactButton>
                  </div>
                </div>
              </SidebarCard>
            </SidebarContent>
          </SolutionContent>
        </motion.div>
      </Container>
    </SolutionSection>
  );
};

export default SolutionDetailPage;